import type { ChangeEvent, ReactNode } from "react";
import { useFormContext } from "@leagueplatform/web-common";
import { useIntl } from "@leagueplatform/locales";
import {
  type GDSTextInputProps,
  FormField,
  TextInput,
} from "@leagueplatform/genesis-core";
import type { RHFInputBaseProps } from "./rhf-input.types";

export type RHFTextInputProps = RHFInputBaseProps & {
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
} & Omit<GDSTextInputProps, "id" | "disabled">;

export const RHFTextInput = ({
  type,
  id,
  label,
  hint,
  required,
  requiredMessage,
  registerOptions,
  onChange,
  disabled,
  ...restComponentProps
}: RHFTextInputProps) => {
  const {
    register,
    formState: { errors },
    setValue,
  } = useFormContext();
  const { formatMessage } = useIntl();

  const defaultRequiredMessage = formatMessage(
    { id: "STR_FIELD_REQUIRED" },
    { fieldName: label }
  );

  const registerProps = register(id, {
    ...registerOptions,
    required: required && (requiredMessage || defaultRequiredMessage),
    ...(onChange && { onChange }),
    onBlur: (e) => {
      const trimmedValue = e.target.value.trim();
      if (trimmedValue !== e.target.value) {
        setValue(id, trimmedValue, { shouldValidate: true });
      }
      registerOptions?.onBlur?.(e);
    },
  });

  const hasError = !!errors?.[id]?.message;

  return (
    <FormField
      id={id}
      label={label ?? ""}
      name={id}
      hideLabel={!label}
      hint={hint}
      required={required}
      inputStatus={hasError ? "error" : undefined}
      statusMessage={hasError ? (errors[id]?.message as ReactNode) : undefined}
    >
      <TextInput
        type={type}
        id={id}
        disabled={disabled}
        {...(hasError && { inputStatus: "error" })}
        {...restComponentProps}
        {...registerProps}
      />
    </FormField>
  );
};
